import React, { useContext, useEffect, useState } from 'react';
import StationsRadioGroup from './StationsRadioGroup';
import StationSelection from './StationSelection';
import { CampaignGoalEnum } from '../../../constants/Enums';
import { AsNumber, AsSelect } from '@adserve/adserve-react-components';
import { SystemSettingsContext } from '../../../contexts/SystemSettingsContext';
import {
    getGoalDescription,
    getTotalDescription,
    getAllocatedDescription,
    getUnallocatedDescription
} from '../../../services/UtilsService';
import { pageContainerStyle, sectionedPanelStyle } from '../CampaignStyles';

// Container of campaignGoal and stationSelection
const container = {
    ...pageContainerStyle,
    marginBottom: 25.5,
    fontFamily: 'Roboto',
    color: '#414141'
};

// Nested container of stationHeader, selectionType, currentGoal and stationTable
const stationSelection = {
    display: 'grid',
    gridTemplateColumns: '2fr 3fr',
    gridTemplateRows: '38px 33px auto',
    ...sectionedPanelStyle,
    padding: '15px 230px'
};

const stationHeader = {
    gridRow: '1 / 2',
    gridColumn: '1 / 2',
    fontSize: 16,
    fontWeight: 500
};
const selectionType = {
    gridRow: '2 / 3',
    gridColumn: '1 / 2',
    fontSize: 13,
    fontWeight: 'bold'
};
const currentGoal = {
    gridRow: '1 / span 2',
    gridColumn: '2 / 3',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center'
};
const stationTable = {
    gridRow: '3 / 4',
    gridColumn: '1 / 3'
};
const numberInputStyle = {
    width: 100,
    border: '1px solid #D7D7D7',
    borderRadius: 5,
    backgroundColor: '#F7F7F7',
    textAlign: 'right',
    paddingRight: 16,
    color: '#121212',
    fontSize: 15,
    fontWeight: 500
};
const numberContainer = { fontSize: 15, fontWeight: 500, marginLeft: 9 };

const CampaignStations = ({
    campaign,
    campaignBurst,
    changeMade,
    recalc,
    recalcOth,
    readOnly = false,
    ledgers,
    setLedgerId,
    isBurst = false
}) => {
    const { formatNumber, formatCurrency } = useContext(SystemSettingsContext);

    const [stationSelectType, setStationSelectType] = useState('');
    const [selectedGoal] = useState(
        isBurst ? campaignBurst.campaignGoal?.find((x) => x.selected) : campaign.campaignGoal?.find((x) => x.selected)
    );
    const [splitOrGoal, setSplitOrGoal] = useState();

    useEffect(() => {
        if (
            selectedGoal.goal === CampaignGoalEnum.BASEOTH ||
            selectedGoal.goal === CampaignGoalEnum.TARGETOTH ||
            selectedGoal.goal === CampaignGoalEnum.OVERALLBASEOTH ||
            selectedGoal.goal === CampaignGoalEnum.OVERALLTARGETOTH ||
            selectedGoal.goal === CampaignGoalEnum.BASEREACH ||
            selectedGoal.goal === CampaignGoalEnum.TARGETREACH
        ) {
            // change column header
            setSplitOrGoal('Goal');
            if (isBurst) {
                recalcOth(campaignBurst.burstStations, false);
            } else {
                recalcOth(campaign.campaignStations, false);
            }
        } else {
            // revert column header
            setSplitOrGoal('Natural Split');
            if (isBurst) {
                recalc(campaignBurst.burstStations, false);
            } else {
                recalc(campaign.campaignStations, false);
            }
        }
    }, []);

    const LedgerSelect = ({ ledgerId }) => {
        if (ledgers) {
            if (ledgers.length > 1) {
                return (
                    <>
                        <AsSelect
                            style={{
                                width: 200,
                                position: 'relative'
                            }}
                            values={ledgers}
                            value={ledgerId}
                            onChange={(e) => setLedgerId(e.target.value)}
                        ></AsSelect>
                    </>
                );
            }
        }

        return <div style={{ height: 50 }}>&nbsp;</div>;
    };

    return (
        <div style={container}>
            <div style={stationSelection}>
                <div style={stationHeader}>{window.translate('Station Selection')}</div>
                <div style={currentGoal}>
                    <div style={{ fontSize: 12 }}>{selectedGoal ? getGoalDescription(selectedGoal.goal) : ''}</div>
                    <div style={numberContainer}>
                        <AsNumber
                            inputStyle={numberInputStyle}
                            disabled={true}
                            formatNumber={formatNumber}
                            formatCurrency={
                                selectedGoal.goal === CampaignGoalEnum.BUDGET ||
                                selectedGoal.goal === CampaignGoalEnum.WEEKLYBUDGET
                                    ? formatCurrency
                                    : null
                            }
                            value={
                                selectedGoal.goal === CampaignGoalEnum.WEEKLYBUDGET
                                    ? selectedGoal?.values?.reduce((a, b) => {
                                          return a + parseFloat(b.value);
                                      }, 0)
                                    : selectedGoal?.value
                            }
                        />
                    </div>
                    {(selectedGoal.goal === CampaignGoalEnum.BUDGET ||
                        selectedGoal.goal === CampaignGoalEnum.WEEKLYBUDGET ||
                        selectedGoal.goal === CampaignGoalEnum.TARGET ||
                        selectedGoal.goal === CampaignGoalEnum.BASE) && (
                        <React.Fragment>
                            <div
                                style={{
                                    fontSize: 12,
                                    width: 88,
                                    marginLeft: 9,
                                    textAlign: 'right'
                                }}
                            >
                                {getTotalDescription(selectedGoal?.goal)}
                            </div>
                            <div style={numberContainer}>
                                <AsNumber
                                    inputStyle={{ ...numberInputStyle, width: 100 }}
                                    disabled={true}
                                    formatNumber={formatNumber}
                                    formatCurrency={
                                        selectedGoal.goal === CampaignGoalEnum.BUDGET ||
                                        selectedGoal.goal === CampaignGoalEnum.WEEKLYBUDGET
                                            ? formatCurrency
                                            : null
                                    }
                                    value={
                                        selectedGoal.goal === CampaignGoalEnum.BUDGET ||
                                        selectedGoal.goal === CampaignGoalEnum.WEEKLYBUDGET
                                            ? isBurst
                                                ? campaignBurst.totalAvailableValue
                                                : campaign.totalAvailableValue
                                            : campaign.totalAvailableImpacts
                                    }
                                />
                            </div>
                            <div
                                style={{
                                    fontSize: 12,
                                    width: 58,
                                    marginLeft: 9,
                                    textAlign: 'right'
                                }}
                            >
                                {getAllocatedDescription(selectedGoal?.goal)}
                            </div>
                            <div style={numberContainer}>
                                <AsNumber
                                    inputStyle={{ ...numberInputStyle, width: 100 }}
                                    disabled={true}
                                    formatNumber={formatNumber}
                                    formatCurrency={
                                        selectedGoal.goal === CampaignGoalEnum.BUDGET ||
                                        selectedGoal.goal === CampaignGoalEnum.WEEKLYBUDGET
                                            ? formatCurrency
                                            : null
                                    }
                                    value={isBurst ? campaignBurst.allocatedBudget : campaign.allocatedBudget}
                                />
                            </div>
                            <div
                                style={{
                                    fontSize: 12,
                                    width: 72,
                                    marginLeft: 9,
                                    textAlign: 'right'
                                }}
                            >
                                {getUnallocatedDescription(selectedGoal?.goal)}
                            </div>
                            <div style={numberContainer}>
                                <AsNumber
                                    inputStyle={numberInputStyle}
                                    disabled={true}
                                    formatNumber={formatNumber}
                                    formatCurrency={
                                        selectedGoal.goal === CampaignGoalEnum.BUDGET ||
                                        selectedGoal.goal === CampaignGoalEnum.WEEKLYBUDGET
                                            ? formatCurrency
                                            : null
                                    }
                                    value={isBurst ? campaignBurst.unallocatedBudget : campaign.unallocatedBudget}
                                />
                            </div>
                        </React.Fragment>
                    )}
                </div>
                <div style={stationTable}>
                    <StationSelection
                        campaign={campaign}
                        campaignBurst={campaignBurst}
                        readOnly={readOnly}
                        recalc={recalc}
                        recalcOth={recalcOth}
                        splitOrGoal={splitOrGoal}
                        setSplitOrGoal={setSplitOrGoal}
                        changeMade={changeMade}
                        selectedGoal={selectedGoal}
                        stationSelectType={stationSelectType}
                        setStationSelectType={setStationSelectType}
                    />
                </div>
            </div>
        </div>
    );
};

export default CampaignStations;
